import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { doc, updateDoc } from 'firebase/firestore';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { useMemo, useState } from 'react';
import { UserModel } from '../models/database/User';
import FirebaseAuth from '../services/FirebaseAuth';
import Loader from '../components/Loader';
import './ExchangeServerRoute.css';

const BASE_URL = 'https://api.miniaccountant.app/v1/exchange_rate';

interface SnackMsg {
  type: 'success' | 'warning' | 'info' | 'error';
  message: string;
  isShown: boolean;
}

const codeBlockSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  backgroundColor: 'action.hover',
  borderRadius: 1,
  padding: '8px 12px',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

function Var({ children }: { children: string }) {
  return (
    <Box
      component="span"
      sx={{ color: 'primary.main', fontWeight: 600 }}
    >
      {children}
    </Box>
  );
}

function CodeBlock({
  children,
  copyText,
  onCopy,
}: {
  children: React.ReactNode;
  copyText?: string;
  onCopy?: (text: string) => void;
}) {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={codeBlockSx}>{children}</Box>
      {copyText && onCopy && (
        <Tooltip title="Copy">
          <IconButton
            size="small"
            onClick={() => onCopy(copyText)}
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            <ContentCopyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function ExchangeServerRoute() {
  const userRef = doc(UserModel.parent, FirebaseAuth.currentUser?.uid);
  const [user] = useDocumentData(userRef);

  const [draft, setDraft] = useState<{ value: string; isChanged: boolean }>({
    value: '',
    isChanged: false,
  });
  const [saving, setSaving] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [showMsg, setShowMsg] = useState<SnackMsg>({
    type: 'success',
    message: '',
    isShown: false,
  });

  const currentValue = user?.customExchangeServer ?? '';
  const editingValue = draft.isChanged ? draft.value : currentValue;
  const trimmed = editingValue.trim();
  const isEnabled = currentValue.length > 0;
  const willDisable = isEnabled && trimmed === '';
  const dirty = draft.isChanged && trimmed !== currentValue;

  const testUrl = useMemo(
    () =>
      `${BASE_URL}/{{ date }}/{{ from }}/{{ to }}?debug=true&token=API_KEY`,
    []
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowMsg({ type: 'success', message: 'Copied!', isShown: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      setShowMsg({ type: 'error', message, isShown: true });
    }
  };

  const apply = async () => {
    setConfirmDisable(false);
    setSaving(true);
    try {
      await updateDoc(userRef, { customExchangeServer: trimmed });
      setShowMsg({
        type: 'success',
        message: trimmed === ''
          ? 'Exchange server disabled.'
          : 'Exchange server updated.',
        isShown: true,
      });
      setDraft({ value: trimmed, isChanged: false });
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'The setting could not be applied. Try again later.';
      setShowMsg({ type: 'error', message, isShown: true });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <Loader />;

  return (
    <div className="ExchangeServerRoute">
      <Alert
        severity="info"
        sx={{ marginBottom: 2 }}
        action={
          <Chip
            label={isEnabled ? 'Enabled' : 'Disabled'}
            color={isEnabled ? 'success' : 'default'}
            size="small"
            sx={{ alignSelf: 'center' }}
          />
        }
      >
        <AlertTitle>Custom exchange server</AlertTitle>
        <Typography variant="body2">
          Plug in your own server to provide currency rates to the application.
          Leave the URL template empty to fall back to the built-in rates.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Test endpoint
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
          Replace <Var>API_KEY</Var> with a personal token from the API Keys
          page. The <Var>debug=true</Var> flag returns a fixed rate of 1 with
          an info message; drop it for live rates. Each test request still
          counts against your API quota.
        </Typography>
        <CodeBlock copyText={testUrl} onCopy={copyToClipboard}>
          {`${BASE_URL}/`}
          <Var>{'{{ date }}'}</Var>/<Var>{'{{ from }}'}</Var>/
          <Var>{'{{ to }}'}</Var>?debug=true&amp;token=API_KEY
        </CodeBlock>
        <Box
          component="img"
          src="/assets/images/custom_exchange_server.png"
          alt="Example of a message in the application"
          sx={{
            display: 'block',
            mt: 2,
            borderRadius: '2vmin',
            maxBlockSize: '40vmin',
          }}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Variables
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
          The server substitutes these placeholders before each request.
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label="{{ date }}"
              size="small"
              sx={{ fontFamily: 'monospace', minWidth: 110 }}
            />
            <Typography variant="body2">
              Date in YYYY-MM-DD format, e.g. 2023-01-01
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label="{{ from }}"
              size="small"
              sx={{ fontFamily: 'monospace', minWidth: 110 }}
            />
            <Typography variant="body2">
              Source currency code, e.g. USD
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label="{{ to }}"
              size="small"
              sx={{ fontFamily: 'monospace', minWidth: 110 }}
            />
            <Typography variant="body2">
              Target currency code, e.g. GEL
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Template examples
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
          You can combine these patterns.
        </Typography>
        <Stack spacing={1.5}>
          <div>
            <Typography variant="caption" color="text.secondary">
              Path parameters
            </Typography>
            <CodeBlock>
              {`${BASE_URL}/`}
              <Var>{'{{ date }}'}</Var>/<Var>{'{{ from }}'}</Var>/
              <Var>{'{{ to }}'}</Var>
            </CodeBlock>
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Query parameters
            </Typography>
            <CodeBlock>
              {`${BASE_URL}?date=`}
              <Var>{'{{ date }}'}</Var>&amp;from=<Var>{'{{ from }}'}</Var>
              &amp;to=<Var>{'{{ to }}'}</Var>
            </CodeBlock>
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Basic authentication
            </Typography>
            <CodeBlock>
              {`${BASE_URL.replace('https://', 'https://USER:PASSWORD@')}/`}
              <Var>{'{{ date }}'}</Var>/<Var>{'{{ from }}'}</Var>/
              <Var>{'{{ to }}'}</Var>
            </CodeBlock>
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Token authentication
            </Typography>
            <CodeBlock>
              {`${BASE_URL}/`}
              <Var>{'{{ date }}'}</Var>/<Var>{'{{ from }}'}</Var>/
              <Var>{'{{ to }}'}</Var>?token=AUTH_TOKEN
            </CodeBlock>
          </div>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Expected response
        </Typography>
        <Stack spacing={1.5}>
          <div>
            <Typography variant="caption" color="text.secondary">
              Request
            </Typography>
            <CodeBlock>
              GET https://api.miniaccountant.app/v1/exchange_rate/2022-03-01/USD/GEL
            </CodeBlock>
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Response headers
            </Typography>
            <CodeBlock>
              {'{ "Content-Type": "application/json" }'}
            </CodeBlock>
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Response body
            </Typography>
            <CodeBlock>
              {'{ "rate": 2.5, "info": "Informational message" }'}
            </CodeBlock>
          </div>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Your URL template
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
          Clear the field and apply to disable the custom server.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={editingValue}
          onChange={(e) =>
            setDraft({ value: e.target.value, isChanged: true })
          }
          placeholder="https://example.com/rate/{{ date }}/{{ from }}/{{ to }}"
          disabled={saving}
          slotProps={{
            input: {
              sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
            },
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, justifyContent: 'flex-end' }}
        >
          <Button
            variant="text"
            disabled={!dirty || saving}
            onClick={() => setDraft({ value: currentValue, isChanged: false })}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color={willDisable ? 'error' : 'primary'}
            disabled={!dirty || saving}
            onClick={() => (willDisable ? setConfirmDisable(true) : apply())}
          >
            {saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : willDisable ? (
              'Disable'
            ) : (
              'Apply'
            )}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={showMsg.isShown}
        autoHideDuration={6000}
        onClose={() => setShowMsg({ ...showMsg, isShown: false })}
        sx={{ width: '100%', padding: '3vmin', justifyContent: 'end' }}
      >
        <Alert
          onClose={() => setShowMsg({ ...showMsg, isShown: false })}
          sx={{ marginRight: '3vmin' }}
          severity={showMsg.type}
        >
          {showMsg.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDisable} onClose={() => setConfirmDisable(false)}>
        <DialogTitle>Disable custom exchange server?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The application will fall back to the built-in exchange rates. You
            can re-enable your server at any time by entering the URL template
            again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(false)}>Cancel</Button>
          <Button color="error" onClick={apply}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ExchangeServerRoute;
